import { X, Wand2, Tag as TagIcon, BarChart3, KeyRound, Ban, Chrome } from 'lucide-react';
import { useUiStore, type SettingsTab } from '../../store/ui';
import { useAuth } from '../../store/auth';
import { RulesTab } from './RulesTab';
import { TagsTab } from './TagsTab';
import { UsageTab } from './UsageTab';
import { AiKeyTab } from './AiKeyTab';
import { BlocklistTab } from './BlocklistTab';
import { ExtensionTab } from './ExtensionTab';

// Solo questo account puo' vedere/gestire la chiave AI usata (BYOK).
const AI_KEY_ADMIN_EMAIL = 'netlab@netlab.it';

const TABS: { key: SettingsTab; label: string; icon: typeof Wand2 }[] = [
  { key: 'rules', label: 'Regole', icon: Wand2 },
  { key: 'tags', label: 'Tag', icon: TagIcon },
  { key: 'blocklist', label: 'Blocklist', icon: Ban },
  { key: 'extension', label: 'Estensione', icon: Chrome },
  { key: 'usage', label: 'Consumo AI', icon: BarChart3 },
  { key: 'aikey', label: 'Chiave AI', icon: KeyRound },
];

// Pannello Impostazioni a schermo con tab (stile dark coerente con AddAccountDialog)
export function SettingsDialog() {
  const open = useUiStore((s) => s.settingsOpen);
  const close = useUiStore((s) => s.closeSettings);
  const tab = useUiStore((s) => s.settingsTab);
  const setTab = useUiStore((s) => s.setSettingsTab);
  const userEmail = useAuth((s) => s.user?.email ?? null);

  // Il tab "Chiave AI" e' riservato all'account amministratore Netlab.
  const canSeeAiKey = userEmail === AI_KEY_ADMIN_EMAIL;
  const visibleTabs = TABS.filter((t) => t.key !== 'aikey' || canSeeAiKey);

  // Se il tab attivo non e' piu' visibile (es. non-admin), ripiega su "Regole".
  const activeTab: SettingsTab = visibleTabs.some((t) => t.key === tab) ? tab : 'rules';

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/80 p-6 backdrop-blur-sm">
      <div className="flex h-[80vh] w-full max-w-4xl animate-fade-in flex-col overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <h2 className="text-sm font-semibold">Impostazioni</h2>
          <button
            onClick={close}
            className="rounded p-1 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-100"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Nav laterale tab */}
          <nav className="flex w-44 shrink-0 flex-col gap-0.5 border-r border-zinc-800 bg-zinc-950/40 p-2">
            {visibleTabs.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`flex items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium transition ${
                  activeTab === key
                    ? 'bg-accent-500/10 text-accent-300'
                    : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-200'
                }`}
              >
                <Icon size={13} />
                {label}
              </button>
            ))}
          </nav>

          {/* Contenuto tab */}
          <div className="flex-1 overflow-y-auto p-5">
            {activeTab === 'rules' && <RulesTab />}
            {activeTab === 'tags' && <TagsTab />}
            {activeTab === 'blocklist' && <BlocklistTab />}
            {activeTab === 'extension' && <ExtensionTab />}
            {activeTab === 'usage' && <UsageTab />}
            {activeTab === 'aikey' && canSeeAiKey && <AiKeyTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
